/**
 * To use this tool tip we have to call this class Tooltip with some config in Vue Mounted method OR in JS just call this class with config
 * example: new Tooltip({selectorsList: ['.className', '.className' ,'#id'], events: ['click','mouseenter', 'mouseleave'],text: ""});
 * if selector is id then use #(hash) if class then use .(dot).
 * selectorsList(where u want to show) and text is required
 * if u are using  mouseenter event then no need to pass mouseleave, code will auto handle mouseleave
 * same for focus event no need to pass blur, code will auto handle blur event
 *
 * Documentation:
 * https://confluence.mtvi.com/pages/viewpage.action?spaceKey=PLWEBAA&title=Tooltip+Vanilla+JS+Component
 * @return {Tooltip}
 */
import Breakpoints from 'helpers/breakpoints';
import { debounce } from '../helpers/Debounce';

/**
 * @param {TooltipConfig}
 */
class Tooltip {
    constructor(obj = new TooltipConfig({ _UDF: false })) {
        if (!obj._UDF) {
            console.warn('Tooltip was not constructed with a TooltipConfig param');
        }

        //
        // privates
        //

        this._defaultConfig = new TooltipConfig({ _UDF: false });
        this._defaultKeyList = Object.keys(this._defaultConfig);
        this._breakpoints = obj._breakpoints ?? new Breakpoints();
        this._observerInnerHtml = null;
        this._observerBody = null;
        this._wasClickToShow = false;
        // try to find and cache tooltip target element
        // if not found on instantiation, will attempt again on show()
        this._targetSelector = obj.targetSelector;
        this._targetElement = null;
        this.setTooltipTargetElement();

        this._classNameTooltip = 'pplus-tooltip';
        this._classNameTooltipInnerHTML = 'pplus-tooltip-inner-html';
        this._classNameCaret = 'pplus-caret';
        this._originalWindowWidth = 0;
        this._paddingFromEdge = {
            mobilePortrait: 20,
            mobileLandscape: 20,
            tabletPortrait: 40,
            tabletLandscape: 40,
            desktop: 95,
            desktopWide: 95,
        };

        // bounds holders
        this._tooltipInnerHtmlBounds = null;
        this._tooltipCaretBounds = null;
        this._tooltipTargetBounds = null;
        this._sameWidthAsBounds = null;
        this._innerHtmlTargetBounds = null;

        // hide tooltip on scroll config
        this.hideOnScroll = obj.hideOnScroll ?? false;

        //
        // end privates
        //

        // keep IDE from complaining
        this.selectorsList = [];

        // validate constructor arg
        if (obj.constructor.name !== this._defaultConfig.constructor.name) {
            console.error(
                'Tooltip constructor argument is not a',
                this._defaultConfig.constructor.name,
                'It is:',
                obj.constructor.name,
            );
        }

        // setup default props
        this._defaultKeyList.forEach((prop) => {
            if (this._defaultConfig.hasOwnProperty(prop)) {
                this[prop] = this._defaultConfig[prop];
            }
        });

        // override props by config
        Object.keys(obj).forEach((prop) => {
            if (this._defaultConfig.hasOwnProperty(prop)) {
                this[prop] = obj[prop];
            } else {
                console.warn('Tooltip: property', prop, 'is not allowed.');
            }
        });

        // bind selectors if any provided
        if (this.selectorsList.length > 0) {
            this.init();
        }
    }

    init() {
        const selectors = document.querySelectorAll(this.selectorsList);

        if (this.events.length > 0) {
            selectors.forEach((node) => {
                if (node) {
                    this.events.forEach((event) => {
                        node.addEventListener(event, this.handleShow, true);
                        if (event === 'mouseenter' || event === 'mouseover' || event === 'focus') {
                            let isOverOrEnter = 'mouseenter' || 'mouseover';
                            node.addEventListener(
                                event === isOverOrEnter ? 'mouseleave' : 'blur',
                                this.handleHide,
                                true,
                            );
                        }
                    });
                }
            });

            // hide tooltip on scroll
            if (this.hideOnScroll) {
                const tooltipInstance = this;
                window.addEventListener('scroll', () => {
                    tooltipInstance.hide();
                });
            }
        }
    }

    // event handlers
    handleShow = (e) => {
        this.show(e);
    };

    handleHide = (e) => {
        this.hide(e);
    };

    handleResize = () => {
        debounce(() => {
            this.reposition(true);
        })();
    };

    handleKeyUp = (e) => {
        if (e.key === 'Escape') {
            this.hide(e);
        }
    };

    handlePopState = (e) => {
        this.hide(e);
    };

    handleBodyClick(e) {
        if (
            !e.target.classList.contains(this._classNameTooltipInnerHTML) &&
            !this.isSelectorOrDescendantOfSelector(e.target)
        ) {
            this.hide(e);
        }
    }

    isSelectorOrDescendantOfSelector(clickedElement) {
        return (
            this.selectorsList.filter((selector) => {
                let el = document.querySelector(selector);
                if (el) {
                    return el === clickedElement || el.contains(clickedElement);
                }
            }).length > 0
        );
    }

    /**
     * this is just a reference function, we are using this on add/remove event calls.
     */
    referenceBodyClick = (e) => {
        this.handleBodyClick(e);
    };

    attachEvents() {
        document.addEventListener('click', this.referenceBodyClick, true);
        document.addEventListener('keyup', this.handleKeyUp, false);
        window.addEventListener('popstate', this.handlePopState, true);
        window.addEventListener('resize', this.handleResize, true);
        this._observerBody = new ResizeObserver(() => {
            debounce(() => {
                this.reposition(true);
            })();
        });
        this._observerBody.observe(this.getBodyElement());
    }

    removeEvents() {
        document.removeEventListener('click', this.referenceBodyClick, true);
        document.removeEventListener('keyup', this.handleKeyUp, false);
        window.removeEventListener('popstate', this.handlePopState, true);
        window.removeEventListener('resize', this.handleResize, true);
    }

    getElementBasePosition(el) {
        let leftPos = 0;
        let topPos = 0;
        while (el) {
            leftPos += el.offsetLeft - el.scrollLeft + el.clientLeft;
            topPos += el.offsetTop - el.scrollTop + el.clientTop;
            el = el.offsetParent;
        }
        return {
            top: topPos,
            left: leftPos,
        };
    }

    /**
     *
     * @returns {HTMLBodyElement}
     */
    getBodyElement() {
        return document.querySelector('body');
    }

    // main tooltip container
    getTooltipElement() {
        return document.querySelector('.' + this._classNameTooltip);
    }

    // tooltip child container holding copy/html
    getTooltipInnerHTMLElement() {
        return document.querySelector('.' + this._classNameTooltipInnerHTML);
    }

    getCaretElement() {
        return document.querySelector('.' + this._classNameCaret);
    }

    show(e = null) {
        // only append tooltip if tooltip does not exist
        if (!this.getTooltipElement()) {
            this._originalWindowWidth = window.innerWidth;
            this.getBodyElement().appendChild(this.generateTooltipHTML());
            this.setupObserver();
            this.reposition();
            this.attachEvents();
            // fire tracking
            if (this.trackOnShow) {
                // only fire tracking if tooltip has a chance to fully show
                let tooltipClass = document.getElementsByClassName('pplus-tooltip')[0];

                if (tooltipClass) {
                    setTimeout(() => {
                        if (tooltipClass.checkVisibility()) {
                            this.trackOnShow(e);
                        }
                    }, 200);
                }
            }
        }

        // toggle tooltip to hide if shown with click, and click again
        if (e && e?.type === 'click') {
            this._wasClickToShow = !this._wasClickToShow;
            if (!this._wasClickToShow) {
                this.hide(e);
            }
        }
    }

    hide(e = null) {
        // only hide tooltip if it exists
        let tooltip = this.getTooltipElement();
        if (tooltip && this.shouldHide(e)) {
            this._wasClickToShow = false;
            this.doReset();
            this.killObservers(); // must come before element is removed
            this.removeEvents();
            tooltip.remove();
            // fire tracking
            if (this.trackOnHide) {
                this.trackOnHide(e);
            }
        }
    }

    shouldHide(e = null) {
        let allowedEventsWhenOpen = ['click', 'keyup', 'popstate'];
        return (
            !this._wasClickToShow ||
            (this._wasClickToShow && allowedEventsWhenOpen.indexOf(e?.type) !== -1)
        );
    }

    // generate html

    generateTooltipHTML() {
        let wrapper = document.createElement('div');
        wrapper.classList.add(this._classNameTooltip, this.position);

        //child div
        wrapper.appendChild(this.generateTooltipBoxHTML());

        //adding caret
        wrapper.appendChild(this.generateCaretHTML());
        return wrapper;
    }

    generateTooltipBoxHTML() {
        let childDiv = document.createElement('div');
        childDiv.classList.add(this._classNameTooltipInnerHTML);
        childDiv.innerHTML = this.getInnerHtml();
        return childDiv;
    }

    generateCaretHTML() {
        //caret div
        let caret = document.createElement('div');
        caret.classList.add(this._classNameCaret);

        if (this.flipCaret === true) {
            caret.classList.add('bottom');
        }
        return caret;
    }

    getInnerHtml() {
        return this.innerHTML;
    }

    //
    // getters setters for elements and bounds
    //

    setTooltipTargetElement() {
        this._targetElement = document.querySelector(this._targetSelector);
        if (!this._targetElement) {
            console.warn(
                `Tooltip:setTooltipTargetElement could not find element using ${this._targetSelector}. This might be OK if called from constructor. Not OK if called from show()`,
            );
        }
        return this._targetElement;
    }

    getTooltipTargetElement() {
        return this._targetElement || this.setTooltipTargetElement();
    }

    setTooltipInnerHtmlBounds() {
        let innerHtmlEle = this.getTooltipInnerHTMLElement();
        if (innerHtmlEle) {
            this._tooltipInnerHtmlBounds = innerHtmlEle.getBoundingClientRect();
            return this._tooltipInnerHtmlBounds;
        }
        return null;
    }

    getTooltipInnerHtmlBounds() {
        return this._tooltipInnerHtmlBounds || this.setTooltipInnerHtmlBounds();
    }

    getInnerHtmlWidth() {
        return this.getTooltipInnerHtmlBounds().width;
    }

    setTooltipTargetBounds() {
        this._tooltipTargetBounds = this.getTooltipTargetElement().getBoundingClientRect();
        return this._tooltipTargetBounds;
    }

    getTooltipTargetBounds() {
        return this._tooltipTargetBounds || this.setTooltipTargetBounds();
    }

    setTooltipCaretBounds() {
        this._tooltipCaretBounds = this.getCaretElement().getBoundingClientRect();
        return this._tooltipCaretBounds;
    }

    getTooltipCaretBounds() {
        return this._tooltipCaretBounds || this.setTooltipCaretBounds();
    }

    // inner html target
    setInnerHtmlTargetBounds() {
        let selector = this.innerHtmlTargetSelector[this._breakpoints.getCurrentBreakpointName()];
        this._innerHtmlTargetBounds = document.querySelector(selector).getBoundingClientRect();
        return this._innerHtmlTargetBounds;
    }

    getInnerHtmlTargetBounds() {
        return this._innerHtmlTargetBounds || this.setInnerHtmlTargetBounds();
    }

    hasInnerHtmlTarget() {
        return this.innerHtmlTargetSelector[this._breakpoints.getCurrentBreakpointName()];
    }

    // same as selector
    setSameAsSelectorBounds() {
        let breakpoint = this._breakpoints.getCurrentBreakpointName();
        this._sameWidthAsBounds = document
            .querySelector(this.sameWidthAsSelector[breakpoint])
            .getBoundingClientRect();
        return this._sameWidthAsBounds;
    }

    getSameAsSelectorWidth() {
        return this._sameWidthAsBounds?.width || this.setSameAsSelectorBounds().width;
    }

    useSameAsSelectorWidth() {
        return this.sameWidthAsSelector[this._breakpoints.getCurrentBreakpointName()];
    }

    //
    // reposition
    //

    reposition(reset = false) {
        let tooltipTargetElement = this._targetElement;

        // only apply tooltip to DOM if the tooltip target element exists
        if (tooltipTargetElement) {
            let tooltip = this.getTooltipElement();

            // only reposition if tooltip exists in DOM
            if (tooltip) {
                if (reset) {
                    this.doReset();
                }
                this.repositionCaret();
                this.setInnerHtmlWidth();

                // reposition inner html container
                // if debounce does not kick in
                setTimeout(() => {
                    this.repositionInnerHtml(); // not sure if this is the best place for this line, but working
                }, 1);
            }
        }
    }

    doReset() {
        this._tooltipInnerHtmlBounds = null;
        this._tooltipTargetBounds = null;
        this._tooltipCaretBounds = null;
        this._sameWidthAsBounds = null;
        this._innerHtmlTargetBounds = null;

        // semi-hack to resize window back to expected width when tooltip is not in viewport
        // this happens when the tooltip could be positioned outside the viewport on resize / orientation change (resize)
        // because we do not use user-scalable=no in the meta viewport
        let t = this.getTooltipInnerHTMLElement();
        t.style.display = 'none';
        this._originalWindowWidth = window.innerWidth;
        t.style.display = 'block';
    }

    setInnerHtmlWidth() {
        let innerHtmlElement = this.getTooltipInnerHTMLElement();

        // set width of tooltip inner html based on widths property
        let tooltipWidth = parseInt(this.widths[this._breakpoints.getCurrentBreakpointName()]);

        // set width of tooltip inner html based on another element
        if (this.useSameAsSelectorWidth()) {
            tooltipWidth = this.getSameAsSelectorWidth();
        }

        // if tooltip is desired to be wider than the viewport
        // then set the width of the tooltip to be the width of the viewport, minus breakpoint padding
        let maxWidth =
            this._originalWindowWidth -
            this._paddingFromEdge[this._breakpoints.getCurrentBreakpointName()] * 2;
        if (tooltipWidth > maxWidth) {
            tooltipWidth = maxWidth;
        }

        innerHtmlElement.style.width = tooltipWidth + 'px';
    }

    repositionInnerHtml() {
        // this.setInnerHtmlWidth();
        let padding = this._paddingFromEdge[this._breakpoints.getCurrentBreakpointName()];
        let innerHtmlElement = this.getTooltipInnerHTMLElement();

        let innerHtmlBounds = this.getTooltipInnerHtmlBounds();
        if (innerHtmlBounds && innerHtmlElement) {
            let caretBounds = this.getTooltipCaretBounds();
            let targetBounds = this.getTooltipTargetBounds();

            let base = this.getElementBasePosition(this.getTooltipTargetElement());
            let top = base.top;
            let left = base.left;

            // default position
            top = top - innerHtmlBounds.height - caretBounds.height / 2;
            left = left + targetBounds.width / 2 - innerHtmlBounds.width / 2;

            // set left offset to match another element
            if (this.hasInnerHtmlTarget()) {
                left = this.getInnerHtmlTargetBounds().left;
            }

            // adjust left offset if tooltip will be out of viewport to the right
            let tooltipWidth = this.getInnerHtmlWidth();
            if (left + tooltipWidth >= this._originalWindowWidth) {
                left = this._originalWindowWidth - tooltipWidth - padding; //base.left - tooltipWidth + padding * 2
            }

            // adjust left offset if tooltip will be out of viewport to the left
            // or aligns inside the margin
            if (left < padding) {
                left = padding;
            }

            if (this.position === 'bottom') {
                let caretBounds = this.getTooltipCaretBounds();
                let caretHeight = caretBounds.height;
                top = base.top + caretHeight * 2 + this.getTooltipTargetBounds().height;
            }

            top = top + this.getOffSetTop();

            innerHtmlElement.style.top = Math.round(top) + 'px';
            innerHtmlElement.style.left = Math.round(left) + 'px';
        }
    }

    /**
     * getting offset top from config if there is any otherwise default will work
     */
    getOffSetTop() {
        let offsetTop = this.offSets[this._breakpoints.getCurrentBreakpointName()].top;
        if (offsetTop.includes('px')) {
            offsetTop = offsetTop.split('px')[0];
        }
        return parseInt(offsetTop);
    }

    // observers
    setupObserver() {
        if (!this._observerInnerHtml) {
            this._observerInnerHtml = new ResizeObserver(() => {
                debounce(() => {
                    this.repositionInnerHtml();
                }, 1)();
            });
        }
        this._observerInnerHtml.observe(this.getTooltipInnerHTMLElement());
    }

    killObservers() {
        if (this._observerInnerHtml) {
            this._observerInnerHtml.unobserve(this.getTooltipInnerHTMLElement());
        }
        if (this._observerBody) {
            this._observerBody.unobserve(this.getBodyElement());
        }
    }

    // position the caret centered over the targetSelector
    repositionCaret() {
        let targetBounds = this.getTooltipTargetBounds();
        let targetWidth = targetBounds.width;

        let caret = this.getCaretElement();
        let caretBounds = this.getTooltipCaretBounds();
        let caretWidth = Math.round(caretBounds.width);
        let caretHeight = caretBounds.height;

        let basePosition = this.getElementBasePosition(this.getTooltipTargetElement());
        let left = basePosition.left + targetWidth / 2 - caretWidth / 2;
        let top = basePosition.top - caretHeight / 2;

        if (this.position === 'bottom') {
            top = basePosition.top + caretHeight + this.getTooltipTargetBounds().height;
        }

        top = top + this.getOffSetTop();
        caret.style.left = Math.round(left) + 'px';
        caret.style.top = Math.round(top) + 'px';
    }
}

class TooltipConfig {
    constructor(obj = {}) {
        // private
        this._UDF = obj._UDF ?? true; // user defined flag to console messages
        this._breakpoints = new Breakpoints();
        this._defaultPosition = 'top';
        this._defaultEvents = ['click'];
        this._defaultWidths = {
            mobilePortrait: '220px',
            mobileLandscape: '220px',
            tabletPortrait: '220px',
            tabletLandscape: '220px',
            desktop: '220px',
            desktopWide: '220px',
        };
        this._defaultOffSet = {
            mobilePortrait: {
                top: '-6px',
            },
            mobileLandscape: {
                top: '-6px',
            },
            tabletPortrait: {
                top: '-6px',
            },
            tabletLandscape: {
                top: '-6px',
            },
            desktop: {
                top: '-6px',
            },
            desktopWide: {
                top: '-6px',
            },
        };
        this._defaultSameWidthAsSelector = {
            mobilePortrait: '',
            mobileLandscape: '',
            tabletPortrait: '',
            tabletLandscape: '',
            desktop: '',
            desktopWide: '',
        };
        this._defaultInnerHtmlTargetSelector = {
            mobilePortrait: '',
            mobileLandscape: '',
            tabletPortrait: '',
            tabletLandscape: '',
            desktop: '',
            desktopWide: '',
        };

        //
        // required props
        //

        if (!obj.targetSelector) {
            this.warn('TooltipConfig:targetSelector is not defined.');
        }
        /**
         * targetSelector reflects a valid CSS selector for the element
         * in which you want the tooltip to appear over
         */
        this.targetSelector = obj.targetSelector ?? '';

        if (!obj.innerHTML) {
            this.warn('TooltipConfig:innerHTML is not defined or empty.');
        }
        this.innerHTML = obj.innerHTML ?? ''; // this property can contain HTML ex: <strong>text</strong>

        //
        // optional props
        //

        this.validateSelectorsList(obj.selectorsList);
        this.selectorsList = obj.selectorsList ?? [];

        this.isValidPosition(obj.position);
        this.position = obj.position ?? this.getDefaultTooltipPosition();

        this.validateEvents(obj.events);
        this.events = obj.events ?? this.getDefaultTooltipEvent();

        this.validateTracking(obj.trackOnShow, obj.trackOnHide);
        this.trackOnShow = obj.trackOnShow ?? null;
        this.trackOnHide = obj.trackOnHide ?? null;

        // all width properties are optional
        this.validateWidths(obj.widths);
        this.widths = obj.widths ? this.setupWidths(obj.widths) : this.getDefaultTooltipWidths();

        // all offSet properties are optional
        this.validateOffSets(obj.offSets);
        this.offSets = obj.offSets ? this.setupOffSets(obj.offSets) : this._defaultOffSet;

        // same as width selector
        this.validateSameWidthAsSelector(obj.sameWidthAsSelector);
        this.sameWidthAsSelector = obj.sameWidthAsSelector
            ? this.setupSameWidthAsSelector(obj.sameWidthAsSelector)
            : this._defaultSameWidthAsSelector;

        // inner html target selector
        this.validateInnerHtmlTargetSelector(obj.innerHtmlTargetSelector);
        this.innerHtmlTargetSelector = obj.innerHtmlTargetSelector
            ? this.setupInnerHtmlTargetSelector(obj.innerHtmlTargetSelector)
            : this._defaultInnerHtmlTargetSelector;

        // hide tooltip on scroll config
        this.hideOnScroll = obj.hideOnScroll ?? false;

        // flip caret for bottom tooltips
        this.flipCaret = obj.flipCaret ?? false;

        // TODO: try to implement
        // this.setupByBreakpoint('innerHtmlTargetSelector', this._defaultInnerHtmlTargetSelector, obj.innerHtmlTargetSelector)
    }

    // TODO: verify all supplied keys are checked for validity, else console error

    validateSelectorsList(selectorsList) {
        if (selectorsList && !Array.isArray(selectorsList)) {
            this.warn('TooltipConfig:selectorsList must be an array.');
        }
    }

    isValidUnit(unit) {
        let unitArr = unit.split('px');
        const baseMsg = 'TooltipConfig:isValidUnit -';
        let msg = baseMsg;
        if (unitArr.length !== 2) {
            msg += ` The unit ${unit} is not valid.`;
        }
        if (unit.indexOf('px') === -1) {
            msg += ` The unit ${unit} must contain 'px'.`;
        }
        if (msg !== baseMsg) {
            this.warn(msg);
        }
    }

    validateWidths(widths) {
        if (widths) {
            if (typeof widths !== 'object') {
                this.warn('Widths is not an object.');
            } else {
                Object.keys(widths).forEach((breakpoint) => {
                    // validate breakpoint name is valid
                    if (!this._breakpoints.getBreakpoint(breakpoint)) {
                        this.warn(
                            'TooltipConfig:validateWidths - Breakpoint',
                            breakpoint,
                            'is not valid',
                        );
                    }
                    // validate width is properly formatted
                    this.isValidUnit(widths[breakpoint]);
                });
            }
        }
    }

    setupWidths(widths) {
        let setup = {};
        Object.keys(this._breakpoints.getBreakpoints()).forEach((breakpoint) => {
            setup[breakpoint] = widths.hasOwnProperty(breakpoint)
                ? (setup[breakpoint] = widths[breakpoint])
                : this._defaultWidths[breakpoint];
        });
        return setup;
    }

    setupOffSets(offSets) {
        let setup = {};
        Object.keys(this._breakpoints.getBreakpoints()).forEach((breakpoint) => {
            let defaultOffSet = this._defaultOffSet[breakpoint];
            if (!offSets.hasOwnProperty(breakpoint)) {
                setup[breakpoint] = defaultOffSet;
            } else {
                let offSet = offSets[breakpoint];
                setup[breakpoint] = {
                    top: offSet.hasOwnProperty('top') ? offSet.top : defaultOffSet.top,
                };
            }
        });
        return setup;
    }

    /**
     * BUGGY ATM
     * @param prop TooltipConfig property
     * @param def default
     * @param udf user defined
     */
    setupByBreakpoint(prop, def, udf) {
        this[prop] = {};
        Object.keys(this._breakpoints.getBreakpoints()).forEach((breakpoint) => {
            this[prop][breakpoint] =
                def.hasOwnProperty(breakpoint) && udf.hasOwnProperty(breakpoint)
                    ? udf[breakpoint]
                    : def[breakpoint];
        });
    }

    validateSameWidthAsSelector(sameWidthAsSelector) {
        if (sameWidthAsSelector) {
            if (typeof sameWidthAsSelector !== 'object') {
                this.warn('TooltipConfig:sameWidthAsSelector is not an object');
            } else {
                Object.keys(sameWidthAsSelector).forEach((key) => {
                    if (
                        sameWidthAsSelector.hasOwnProperty(key) &&
                        !this._defaultSameWidthAsSelector.hasOwnProperty(key)
                    ) {
                        this.warn(
                            `TooltipConfig:sameWidthAsSelector property '${key}' is not valid`,
                        );
                    }
                });
            }
        }
    }

    setupSameWidthAsSelector(sameWidthAsSelector) {
        let result = {};
        Object.keys(this._defaultSameWidthAsSelector).forEach((key) => {
            result[key] =
                this._defaultSameWidthAsSelector.hasOwnProperty(key) &&
                sameWidthAsSelector.hasOwnProperty(key)
                    ? sameWidthAsSelector[key]
                    : this._defaultSameWidthAsSelector[key];
        });
        return result;
    }

    validateInnerHtmlTargetSelector(innerHtmlTargetSelector) {
        if (innerHtmlTargetSelector) {
            if (typeof innerHtmlTargetSelector !== 'object') {
                this.warn('TooltipConfig:innerHtmlTargetSelector is not an object');
            } else {
                Object.keys(innerHtmlTargetSelector).forEach((key) => {
                    if (
                        innerHtmlTargetSelector.hasOwnProperty(key) &&
                        !this._defaultInnerHtmlTargetSelector.hasOwnProperty(key)
                    ) {
                        this.warn(`TooltipConfig:innerHtmlTarget property '${key}' is not valid`);
                    }
                });
            }
        }
    }

    setupInnerHtmlTargetSelector(innerHtmlTargetSelector) {
        let result = {};
        Object.keys(this._defaultInnerHtmlTargetSelector).forEach((key) => {
            result[key] =
                this._defaultInnerHtmlTargetSelector.hasOwnProperty(key) &&
                innerHtmlTargetSelector.hasOwnProperty(key)
                    ? innerHtmlTargetSelector[key]
                    : this._defaultInnerHtmlTargetSelector[key];
        });
        return result;
    }

    validateOffSets(offSets) {
        if (offSets) {
            if (typeof offSets !== 'object') {
                this.warn('offSets is not an object.');
            } else {
                Object.keys(offSets).forEach((breakpoint) => {
                    // validate breakpoint name is valid
                    if (!this._breakpoints.getBreakpoint(breakpoint)) {
                        this.warn(
                            'TooltipConfig:validateOffSets - Breakpoint',
                            breakpoint,
                            'is not valid',
                        );
                    }
                    // validate child props (left and top)
                    Object.keys(offSets[breakpoint]).forEach((key) => {
                        if (key !== 'top') {
                            this.warn(
                                'TooltipConfig:validateOffSets - property',
                                key,
                                'is not valid',
                            );
                        }
                        // validate top and left is properly formatted
                        this.isValidUnit(offSets[breakpoint][key]);
                    });
                });
            }
        }
    }

    warn(msg) {
        // only throw warning for user defined configs
        if (this._UDF) {
            console.warn(msg);
        }
    }

    getDefaultTooltipPosition() {
        return this._defaultPosition;
    }

    getDefaultTooltipEvent() {
        return this._defaultEvents;
    }

    getDefaultTooltipWidths() {
        return this._defaultWidths;
    }

    isValidPosition(pos) {
        let bool = true;
        const validPositions = ['top', 'bottom'];
        if (pos) {
            bool = validPositions.includes(pos);
            if (!bool) {
                this.warn('TooltipConfig position', pos, 'is not a valid position');
            }
        }
        return bool;
    }

    validateEvents(events) {
        if (events) {
            if (!Array.isArray(events)) {
                this.warn('TooltipConfig events are not valid. Must be an array.');
                return false;
            } else {
                events.forEach((event) => {
                    let bool = this.isValidEvent(event);
                    if (!bool) {
                        this.warn('TooltipConfig:validateEvents invalid event', event);
                    }
                });
            }
        }
        return true;
    }

    isValidEvent(event) {
        let validEvents = ['click', 'mouseover', 'mouseenter', 'focus'];
        return validEvents.includes(event);
    }

    validateTracking(trackOnShow, trackOnHide) {
        this.validateTrackingMethod(trackOnShow).validateTrackingMethod(trackOnHide);
    }

    validateTrackingMethod(trackingMethod) {
        if (trackingMethod) {
            if (typeof trackingMethod !== 'function') {
                this.warn(
                    'TooltipConfig:validateTracking tracking method is not a function',
                    trackingMethod,
                );
            }
        }
        return this;
    }
}

export { TooltipConfig, Tooltip };
